import { useEffect } from 'react';

/**
 * Hook SEO simple sans dépendance npm.
 *
 * Met à jour dynamiquement les balises meta de la page courante :
 * - <title>
 * - meta description
 * - meta keywords
 * - canonical URL
 * - Open Graph (og:title, og:description, og:url, og:image)
 * - Twitter Card (twitter:title, twitter:description, twitter:image)
 * - JSON-LD structured data
 *
 * Au démontage du composant, restaure les valeurs de l'index.html (SSR-friendly).
 *
 * @example
 *   useSeo({
 *     title: 'Programme ETM 2026 — 9 thèmes officiels',
 *     description: 'Programme officiel de l\'Épreuve Théorique Moto...',
 *     path: '/programme-etm',
 *     jsonLd: { ... }
 *   });
 */

const SITE_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SITE_URL) ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  'https://code2roues.fr';

const SITE_NAME = 'CODE 2 ROUES';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

function setMeta(selector, attr, value) {
  if (!value) return null;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    // selector est de la forme `meta[name="..."]` ou `meta[property="..."]`
    const m = selector.match(/^meta\[(\w+)="([^"]+)"\]$/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, previous, created };
}

function setLinkCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  const previous = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, previous, created };
}

function injectJsonLd(jsonLd, dataKey) {
  if (!jsonLd) return null;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-key', dataKey);
  script.textContent = JSON.stringify(jsonLd);
  document.head.appendChild(script);
  return script;
}

/**
 * Préchargement d'une image LCP via <link rel="preload" as="image">.
 * À utiliser pour l'image hero d'une page (au-dessus de la ligne de flottaison).
 * Suppression automatique au démontage pour éviter les preloads orphelins
 * (ce qui économise la bande passante quand on quitte la page).
 *
 * @param {string} href URL de l'image à précharger
 * @param {string} dataKey identifiant unique pour le data-attribute
 */
function injectImagePreload(href, dataKey) {
  if (!href) return null;
  // Évite de créer un doublon si déjà présent (changement de route → re-render)
  const existing = document.head.querySelector(
    `link[rel="preload"][data-seo-key="${dataKey}"]`,
  );
  if (existing) return existing;

  const link = document.createElement('link');
  link.setAttribute('rel', 'preload');
  link.setAttribute('as', 'image');
  link.setAttribute('href', href);
  link.setAttribute('fetchpriority', 'high');
  link.setAttribute('data-seo-key', dataKey);
  document.head.appendChild(link);
  return link;
}

export function useSeo({
  title,
  description,
  keywords,
  path = '',
  image,
  preloadImage,
  jsonLd,
  noindex = false,
} = {}) {
  useEffect(() => {
    const previousTitle = document.title;
    const restorers = [];
    const fullTitle = title
      ? `${title} | ${SITE_NAME}`
      : document.title;
    const fullUrl = `${SITE_URL}${path || ''}`;
    // og:image doit être une URL absolue pour être lue par les crawlers
    // Facebook/Twitter/LinkedIn. On préfixe SITE_URL si on reçoit un path relatif.
    const toAbsoluteUrl = (url) => {
      if (!url) return DEFAULT_OG_IMAGE;
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    };
    const ogImg = toAbsoluteUrl(image);

    if (title) document.title = fullTitle;

    const updates = [
      ['meta[name="description"]', 'content', description],
      ['meta[name="keywords"]', 'content', keywords],
      ['meta[property="og:title"]', 'content', title || null],
      ['meta[property="og:description"]', 'content', description],
      ['meta[property="og:url"]', 'content', fullUrl],
      ['meta[property="og:image"]', 'content', ogImg],
      ['meta[name="twitter:title"]', 'content', title || null],
      ['meta[name="twitter:description"]', 'content', description],
      ['meta[name="twitter:image"]', 'content', ogImg],
    ];
    if (noindex) {
      updates.push(['meta[name="robots"]', 'content', 'noindex, nofollow']);
    }
    updates.forEach(([sel, attr, val]) => {
      const r = setMeta(sel, attr, val);
      if (r) restorers.push(r);
    });

    // Canonical
    const canon = setLinkCanonical(fullUrl);
    if (canon) restorers.push(canon);

    // JSON-LD (insertion + nettoyage)
    const jsonLdScript = injectJsonLd(jsonLd, `dyn-${path || '/'}`);

    // Preload de l'image LCP (route-spécifique). On utilise une clé unique
    // par route pour permettre au navigateur de prioriser correctement.
    const preloadLink = injectImagePreload(preloadImage, `lcp-${path || '/'}`);

    return () => {
      // Restaurer les valeurs précédentes
      document.title = previousTitle;
      restorers.forEach(({ el, previous, created }) => {
        if (created) {
          el.parentNode?.removeChild(el);
        } else if (previous != null) {
          // Le bon attribut dépend du type
          if (el.tagName === 'LINK') el.setAttribute('href', previous);
          else el.setAttribute('content', previous);
        }
      });
      if (jsonLdScript) jsonLdScript.parentNode?.removeChild(jsonLdScript);
      if (preloadLink) preloadLink.parentNode?.removeChild(preloadLink);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, path, image, preloadImage, JSON.stringify(jsonLd), noindex]);
}

export default useSeo;
